import { format } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  CartesianGrid,
  ComposedChart,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { BpmDateString } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import { OutcomeVariableType } from "@blisspointmedia/bpm-types/dist/Forecasting";
import { CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from "../../Components/Charts/ChartConstants";
import { useDynamicYAxisWidth } from "../../Components/Charts/ChartUtils";
import { ShadedPattern } from "../../MMM/FillPatterns/ShadedPattern";
import { useCompanyInfo } from "../../redux/company";
import { Brand50, Brand70, colorMap as colors, Neutral500 } from "../../utils/colors";
import { dateToMddyy, parseEpochDate } from "../../utils/date-utils";
import { convertDateToUTC } from "../../utils/format-utils";
import {
  ForecastChartViewLabels,
  ForecastData,
  ForecastKpiViewOption,
  formatAxisCpm,
  formatAxisSpend,
  HISTORICAL_KEY,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES
} from "../forecastingConstants";
import { getKpiFormatter } from "../forecastingUtils";
import {
  buildKpiComparisonGraphs,
  buildKpiPrimaryOnlyGraphs,
  buildSpendComparisonGraphs,
  buildSpendPrimaryOnlyGraphs
} from "./ForecastResultsInternalGraphs";
import {
  buildComparisonLegend,
  buildKpiPrimaryOnlyLegend,
  spendPrimaryOnlyLegend
} from "./ForecastResultsLegend";
import {
  buildKpiComparisonTooltipRenderer,
  buildKpiPrimaryOnlyTooltipRenderer,
  buildSpendComparisonTooltipRenderer,
  buildSpendPrimaryOnlyTooltipRenderer
} from "./ForecastResultsTooltips";

const RIGHT_MARGIN = 30;
const TOP_MARGIN = 20;
const DEFAULT_HEIGHT = "261px";

interface ForecastResultsChartProps {
  data: ForecastData<string>[];
  dateGrouping: string;
  syncId?: number;
  height?: string;
  channel: string;
  outcomeVar: OutcomeVariableType;
  selectedKpiView: ForecastKpiViewOption;
  allComparisonBudgets: number[];
  selectedComparisonBudgets: number[];
  earliestVisibleDate: BpmDateString;
  setEarliestVisibleDate: (BpmDateString) => void;
  earliestPredictedDate: BpmDateString;
  latestVisibleDate: BpmDateString;
  setLatestVisibleDate: (BpmDateString) => void;
  primaryBudget: number;
}

const ForecastResultsChart: React.FC<ForecastResultsChartProps> = ({
  data,
  dateGrouping,
  syncId,
  height = DEFAULT_HEIGHT,
  channel,
  outcomeVar,
  selectedKpiView,
  allComparisonBudgets,
  selectedComparisonBudgets,
  earliestVisibleDate,
  setEarliestVisibleDate,
  earliestPredictedDate,
  latestVisibleDate,
  setLatestVisibleDate,
  primaryBudget,
}) => {
  const [hoveredBudget, setHoveredBudget] = useState<number | undefined>();
  const [selectedBudget, setSelectedBudget] = useState<number | undefined>();
  const [mouseDownTimestamp, setMouseDownTimestamp] = useState<number | undefined>();
  const [mouseMoveTimestamp, setMouseMoveTimestamp] = useState<number | undefined>();
  const [kpiYAxisDomain, setKpiYAxisDomain] = useState<[number | string, number | string]>([
    "dataMin",
    "dataMax",
  ]);
  const [spendYAxisDomain, setSpendYAxisDomain] = useState<[number | string, number | string]>([
    "dataMin",
    "dataMax",
  ]);
  const [cpmYAxisDomain, setCpmYAxisDomain] = useState<[number | string, number | string]>([
    "dataMin",
    "dataMax",
  ]);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const containerRef = useRef<HTMLDivElement>(null);

  const visibleBudgets = useMemo(
    () => (selectedBudget ? [selectedBudget] : selectedComparisonBudgets),
    [selectedBudget, selectedComparisonBudgets]
  );
  const primaryBudgetOnly = useMemo(() => selectedComparisonBudgets.length === 0, [
    selectedComparisonBudgets.length,
  ]);

  const earliestVisibleTimestamp = useMemo(() => new Date(earliestVisibleDate).getTime(), [
    earliestVisibleDate,
  ]);
  const earliestPredictedTimestamp = useMemo(() => new Date(earliestPredictedDate).getTime(), [
    earliestPredictedDate,
  ]);
  const latestVisibleTimestamp = useMemo(() => new Date(latestVisibleDate).getTime(), [
    latestVisibleDate,
  ]);

  const xStartIndex = useMemo(() => {
    return data.findIndex(row => row.timestamp === earliestVisibleTimestamp);
  }, [data, earliestVisibleTimestamp]);
  const xEndIndex = useMemo(() => {
    return data.findIndex(row => row.timestamp === latestVisibleTimestamp);
  }, [data, latestVisibleTimestamp]);

  useEffect(() => {
    if (xStartIndex === -1 || xEndIndex === -1) {
      return;
    }

    const dataWithinXDomain = data.slice(xStartIndex, xEndIndex);

    let kpiMin = Infinity,
      spendMin = Infinity,
      cpmMin = Infinity,
      kpiMax = 0,
      spendMax = 0,
      cpmMax = 0;

    const budgets = primaryBudgetOnly ? [primaryBudget] : selectedComparisonBudgets;
    dataWithinXDomain.forEach(row => {
      if (row.timestamp >= earliestPredictedTimestamp) {
        budgets.forEach(budget => {
          const kpiVal = row[`${budget}-${KPI_VIEW_SUFFICES[selectedKpiView]}`];
          const kpiCiVal = row[`${budget}-${KPI_CI_VIEW_SUFFICES[selectedKpiView]}`];
          const spendVal = row[`${budget}-spend`];
          const cpmVal = row[`${budget}-cpm`];

          kpiMin = Math.min(kpiMin, kpiVal, kpiCiVal[0], kpiCiVal[1]);
          kpiMax = Math.max(kpiMax, kpiVal, kpiCiVal[0], kpiCiVal[1]);
          spendMin = Math.min(spendMin, spendVal);
          spendMax = Math.max(spendMax, spendVal);
          cpmMin = Math.min(cpmMin, cpmVal);
          cpmMax = Math.max(cpmMax, cpmVal);
        });
      } else {
        const kpiVal = row[`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[selectedKpiView]}`];
        const spendVal = row[`${HISTORICAL_KEY}-spend`];
        const cpmVal = row[`${HISTORICAL_KEY}-cpm`];

        kpiMin = Math.min(kpiMin, kpiVal);
        kpiMax = Math.max(kpiMax, kpiVal);
        spendMin = Math.min(spendMin, spendVal);
        spendMax = Math.max(spendMax, spendVal);
        cpmMin = Math.min(cpmMin, cpmVal);
        cpmMax = Math.max(cpmMax, cpmVal);
      }
    });

    // Add 5% buffer to either side, but no lower than 0
    kpiMin = Math.max(kpiMin * 0.95, 0);
    spendMin = Math.max(spendMin * 0.95, 0);
    cpmMin = Math.max(cpmMin * 0.95, 0);
    kpiMax = kpiMax * 1.05;
    spendMax = spendMax * 1.05;
    cpmMax = cpmMax * 1.05;

    setKpiYAxisDomain([kpiMin, kpiMax]);
    setSpendYAxisDomain([spendMin, spendMax]);
    setCpmYAxisDomain([cpmMin, cpmMax]);
  }, [
    data,
    earliestPredictedTimestamp,
    primaryBudget,
    primaryBudgetOnly,
    selectedComparisonBudgets,
    selectedKpiView,
    visibleBudgets,
    xEndIndex,
    xStartIndex,
  ]);

  const { yAxisWidth: topYAxisWidth, setChartRef: setTopChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    data,
  });

  const { yAxisWidth: bottomYAxisWidth, setChartRef: setBottomChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    data,
  });

  const maxYAxisWidth = useMemo(() => Math.max(topYAxisWidth, bottomYAxisWidth), [
    bottomYAxisWidth,
    topYAxisWidth,
  ]);

  const colorMap = useMemo(
    () => ({
      ...colors(allComparisonBudgets.map(budget => ({ name: String(budget) }))),
      [primaryBudget]: Brand70,
    }),
    [allComparisonBudgets, primaryBudget]
  );

  const kpiPrimaryOnlyGraphs = useMemo(
    () => buildKpiPrimaryOnlyGraphs(primaryBudget, selectedKpiView),
    [primaryBudget, selectedKpiView]
  );

  const kpiComparisonGraphs = useMemo(
    () =>
      buildKpiComparisonGraphs(
        visibleBudgets,
        hoveredBudget,
        selectedBudget,
        colorMap,
        selectedKpiView
      ),
    [visibleBudgets, hoveredBudget, selectedBudget, colorMap, selectedKpiView]
  );

  const spendPrimaryOnlyGraphs = useMemo(() => buildSpendPrimaryOnlyGraphs(primaryBudget), [
    primaryBudget,
  ]);

  const spendComparisonGraphs = useMemo(
    () => buildSpendComparisonGraphs(visibleBudgets, hoveredBudget, colorMap),
    [colorMap, hoveredBudget, visibleBudgets]
  );

  const kpiPrimaryOnlyLegend = useMemo(
    () => buildKpiPrimaryOnlyLegend(ForecastChartViewLabels[outcomeVar][selectedKpiView]),
    [selectedKpiView, outcomeVar]
  );

  const kpiComparisonLegend = useMemo(
    () =>
      buildComparisonLegend({
        title: "Forecasted KPI Volume",
        visibleBudgets,
        primaryBudget,
        colorMap,
        includeCiIfApplicable: true,
        hoveredBudget,
        setHoveredBudget,
        selectedBudget,
        setSelectedBudget,
      }),
    [colorMap, hoveredBudget, primaryBudget, selectedBudget, visibleBudgets]
  );

  const spendComparisonLegend = useMemo(
    () =>
      buildComparisonLegend({
        title: "Forecasted Spend",
        visibleBudgets,
        primaryBudget,
        colorMap,
        hoveredBudget,
        setHoveredBudget,
        selectedBudget,
        setSelectedBudget,
      }),
    [colorMap, hoveredBudget, primaryBudget, selectedBudget, visibleBudgets]
  );

  const kpiPrimaryOnlyTooltipRenderer = useMemo(
    () =>
      buildKpiPrimaryOnlyTooltipRenderer({
        earliestPredictedDate,
        dateGrouping,
        fiscalCal,
        primaryBudget,
        outcomeVar,
        kpiView: selectedKpiView,
      }),
    [dateGrouping, earliestPredictedDate, fiscalCal, outcomeVar, primaryBudget, selectedKpiView]
  );

  const spendPrimaryOnlyTooltipRenderer = useMemo(
    () =>
      buildSpendPrimaryOnlyTooltipRenderer({
        earliestPredictedDate,
        dateGrouping,
        fiscalCal,
        primaryBudget,
        outcomeVar,
        kpiView: selectedKpiView,
      }),
    [dateGrouping, earliestPredictedDate, fiscalCal, outcomeVar, primaryBudget, selectedKpiView]
  );

  const kpiComparisonTooltipRenderer = useMemo(
    () =>
      buildKpiComparisonTooltipRenderer({
        earliestPredictedDate,
        dateGrouping,
        fiscalCal,
        selectedBudget,
        visibleBudgets,
        colorMap,
        outcomeVar,
        kpiView: selectedKpiView,
      }),
    [
      colorMap,
      dateGrouping,
      earliestPredictedDate,
      fiscalCal,
      outcomeVar,
      selectedBudget,
      selectedKpiView,
      visibleBudgets,
    ]
  );

  const spendComparisonTooltipRenderer = useMemo(
    () =>
      buildSpendComparisonTooltipRenderer({
        earliestPredictedDate,
        dateGrouping,
        fiscalCal,
        selectedBudget,
        visibleBudgets,
        colorMap,
        outcomeVar,
        kpiView: selectedKpiView,
      }),
    [
      colorMap,
      dateGrouping,
      earliestPredictedDate,
      fiscalCal,
      outcomeVar,
      selectedBudget,
      selectedKpiView,
      visibleBudgets,
    ]
  );

  const formatKpi = getKpiFormatter(outcomeVar, selectedKpiView);

  const zoom = () => {
    if (!mouseDownTimestamp || !mouseMoveTimestamp || mouseDownTimestamp === mouseMoveTimestamp) {
      setMouseDownTimestamp(undefined);
      setMouseMoveTimestamp(undefined);
      return;
    }

    // Convert the timestamps to BpmDateStrings
    const earlierTimestamp = Math.min(mouseDownTimestamp, mouseMoveTimestamp);
    const laterTimestamp = Math.max(mouseDownTimestamp, mouseMoveTimestamp);
    const earlierDate = format(
      convertDateToUTC(new Date(earlierTimestamp)),
      "yyyy-MM-dd"
    ) as BpmDateString;
    const laterDate = format(
      convertDateToUTC(new Date(laterTimestamp)),
      "yyyy-MM-dd"
    ) as BpmDateString;

    // Set the min and max visible dates
    setEarliestVisibleDate(earlierDate);
    setLatestVisibleDate(laterDate);

    // Reset the mouse down and move
    setMouseDownTimestamp(undefined);
    setMouseMoveTimestamp(undefined);
  };

  return (
    <div className="forecastResultsGraphContainer">
      <div className="patternDefinitions">
        <ShadedPattern
          color="white"
          backgroundColor={Brand50}
          id={`Neg${Brand50}`}
          visibleTag={false}
        />
      </div>
      <div className="forecastedKPIsWrapper">
        <div className="forecastedKPIsGraph" style={{ height }} ref={containerRef}>
          <div
            className="forecastedKPIsGraphTitle"
            style={{ marginLeft: maxYAxisWidth, marginRight: RIGHT_MARGIN }}
          >
            {channel} {ForecastChartViewLabels[outcomeVar][selectedKpiView]}
          </div>
          <AutoSizer>
            {({ width, height }) => (
              <ComposedChart
                data={data}
                height={height}
                margin={{ top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 }}
                syncId={syncId}
                ref={setTopChartRef}
                width={width}
                onMouseDown={e => e?.activeLabel && setMouseDownTimestamp(Number(e?.activeLabel))}
                onMouseMove={e =>
                  e?.activeLabel &&
                  mouseDownTimestamp &&
                  setMouseMoveTimestamp(Number(e?.activeLabel))
                }
                onMouseUp={zoom}
              >
                <XAxis
                  type="number"
                  dataKey="timestamp"
                  tickCount={7}
                  tick={TICK_STYLE}
                  tickFormatter={val => dateToMddyy(parseEpochDate(val))}
                  domain={[earliestVisibleTimestamp, latestVisibleTimestamp]}
                  allowDataOverflow
                />
                <YAxis
                  type="number"
                  tick={TICK_STYLE}
                  tickFormatter={formatKpi}
                  tickLine={false}
                  axisLine={false}
                  width={maxYAxisWidth}
                  domain={kpiYAxisDomain}
                  allowDataOverflow
                />
                {primaryBudgetOnly ? kpiPrimaryOnlyGraphs : kpiComparisonGraphs}
                {earliestPredictedDate && (
                  <ReferenceLine stroke={Neutral500} x={earliestPredictedDate} strokeWidth={2} />
                )}
                <CartesianGrid
                  stroke={Neutral500}
                  strokeOpacity={0.25}
                  strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                  vertical={false}
                />
                {mouseDownTimestamp && mouseMoveTimestamp && (
                  <ReferenceArea
                    x1={mouseDownTimestamp}
                    x2={mouseMoveTimestamp}
                    strokeOpacity={0.3}
                  />
                )}
                <Tooltip
                  content={({ label, payload }) => {
                    return selectedComparisonBudgets.length > 0
                      ? kpiComparisonTooltipRenderer({ timestamp: label, payload })
                      : kpiPrimaryOnlyTooltipRenderer({ timestamp: label, payload });
                  }}
                  cursor={{ stroke: Neutral500, width: "1px" }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            )}
          </AutoSizer>
        </div>
        <div className="rightYAxisSpacer" style={{ width: maxYAxisWidth }} />
        <div className="rightOfChart">
          {primaryBudgetOnly ? kpiPrimaryOnlyLegend : kpiComparisonLegend}
        </div>
      </div>
      <div className="forecastedSpendWrapper">
        <div className="forecastedSpendGraph" style={{ height }} ref={containerRef}>
          <div
            className="forecastedSpendGraphTitle"
            style={{ marginLeft: maxYAxisWidth, marginRight: RIGHT_MARGIN }}
          >
            {channel} Spend
          </div>
          <AutoSizer>
            {({ width, height }) => (
              <ComposedChart
                data={data}
                height={height}
                margin={{ top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 }}
                syncId={syncId}
                ref={setBottomChartRef}
                width={width}
                onMouseDown={e => e?.activeLabel && setMouseDownTimestamp(Number(e?.activeLabel))}
                onMouseMove={e =>
                  e?.activeLabel &&
                  mouseDownTimestamp &&
                  setMouseMoveTimestamp(Number(e?.activeLabel))
                }
                onMouseUp={zoom}
              >
                <XAxis
                  type="number"
                  dataKey="timestamp"
                  tickCount={7}
                  tick={TICK_STYLE}
                  tickFormatter={val => dateToMddyy(parseEpochDate(val))}
                  domain={[earliestVisibleTimestamp, latestVisibleTimestamp]}
                  allowDataOverflow
                />
                <YAxis
                  type="number"
                  yAxisId={0}
                  tick={TICK_STYLE}
                  tickFormatter={formatAxisSpend}
                  tickLine={false}
                  axisLine={false}
                  width={maxYAxisWidth}
                  domain={spendYAxisDomain}
                  allowDataOverflow
                />
                {primaryBudgetOnly && (
                  <YAxis
                    type="number"
                    yAxisId={1}
                    orientation="right"
                    tick={TICK_STYLE}
                    tickFormatter={formatAxisCpm}
                    tickLine={false}
                    axisLine={false}
                    width={maxYAxisWidth}
                    domain={cpmYAxisDomain}
                    allowDataOverflow
                  />
                )}
                {primaryBudgetOnly ? spendPrimaryOnlyGraphs : spendComparisonGraphs}
                {earliestPredictedDate && (
                  <ReferenceLine stroke={Neutral500} x={earliestPredictedDate} strokeWidth={2} />
                )}
                <CartesianGrid
                  stroke={Neutral500}
                  strokeOpacity={0.25}
                  strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                  vertical={false}
                />
                {mouseDownTimestamp && mouseMoveTimestamp && (
                  <ReferenceArea
                    x1={mouseDownTimestamp}
                    x2={mouseMoveTimestamp}
                    strokeOpacity={0.3}
                  />
                )}
                <Tooltip
                  content={({ label, payload }) =>
                    selectedComparisonBudgets.length > 0
                      ? spendComparisonTooltipRenderer({ timestamp: label, payload })
                      : spendPrimaryOnlyTooltipRenderer({ timestamp: label, payload })
                  }
                  cursor={{ stroke: Neutral500, width: "1px" }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            )}
          </AutoSizer>
        </div>
        {selectedComparisonBudgets.length !== 0 && (
          <div id="rightYAxisSpacer" style={{ width: maxYAxisWidth }} />
        )}
        <div className="rightOfChart">
          {primaryBudgetOnly ? spendPrimaryOnlyLegend : spendComparisonLegend}
        </div>
      </div>
    </div>
  );
};

export default ForecastResultsChart;
